import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Send, Loader2, Paperclip, Download, FileText, X, MessageSquare, Plus, ChevronLeft, BookOpen, Brain, ClipboardList, Stethoscope, Pill, TestTube, Edit2, Check, Shield, CheckCircle2, Upload, Search, Database, Activity, Microscope, ExternalLink, Globe, Maximize2, Minimize2, Users, Square } from 'lucide-react';
import { chatAPI, medicalAPI, fastChatAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import ConsiliumModal from './ConsiliumModal';

// Function to convert ASCII tables and format medical content
const formatMedicalContent = (text) => {
  if (!text) return '';
  
  // Split text into lines
  const lines = text.split('\n');
  const result = [];
  let inTable = false;
  let tableLines = [];
  let inDisclaimer = false;
  let disclaimerLines = [];
  
  const processTable = (tableData) => {
    if (tableData.length < 2) return tableData.join('\n');
    
    // Parse table structure
    const rows = [];
    let headerFound = false;
    
    for (const line of tableData) {
      const trimmed = line.trim();
      // Skip ASCII separator lines like +----+----+----+
      if (/^[+\-|=\s]+$/.test(trimmed) && trimmed.includes('+')) continue;
      // Skip pure dash/equals separator lines
      if (/^[\-=]+$/.test(trimmed)) continue;
      // Skip markdown separator rows like |---|---| or | --- | :--- | ---: |
      if (/^\|?[\s\-:|]+\|?$/.test(trimmed) && /\-/.test(trimmed) && trimmed.includes('|')) {
        // Make sure it's only dashes/colons/pipes/spaces (no real text)
        const stripped = trimmed.replace(/[\s\-:|]/g, '');
        if (stripped.length === 0) continue;
      }
      
      // Parse data row
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
        if (cells.length > 0) {
          rows.push({ cells, isHeader: !headerFound });
          if (!headerFound) headerFound = true;
        }
      }
    }
    
    if (rows.length === 0) return ''; // Empty table → skip rendering entirely
    // Skip rendering tables that have ONLY a header row with no data
    if (rows.length === 1) return '';
    
    // Build HTML table
    let html = '<div class="overflow-x-auto my-3"><table class="w-full border-collapse text-xs sm:text-sm">';
    
    rows.forEach((row, idx) => {
      if (idx === 0) {
        html += '<thead><tr>';
        row.cells.forEach(cell => {
          html += `<th class="border border-white/20 bg-white/10 px-2 py-1.5 text-left text-cyan-300 font-semibold">${cell}</th>`;
        });
        html += '</tr></thead><tbody>';
      } else {
        const bgClass = idx % 2 === 0 ? 'bg-white/[0.02]' : 'bg-white/[0.05]';
        html += `<tr class="${bgClass}">`;
        row.cells.forEach(cell => {
          // Highlight abnormal values
          let cellClass = 'text-white/80';
          if (/повыш|↑|высок|выше нормы/i.test(cell)) cellClass = 'text-red-400 font-medium';
          else if (/пониж|↓|низк|ниже нормы/i.test(cell)) cellClass = 'text-yellow-400 font-medium';
          else if (/норма|нормальн|в норме/i.test(cell)) cellClass = 'text-emerald-400';
          html += `<td class="border border-white/10 px-2 py-1.5 ${cellClass}">${cell}</td>`;
        });
        html += '</tr>';
      }
    });
    
    html += '</tbody></table></div>';
    return html;
  };

  const flushDisclaimer = () => {
    if (disclaimerLines.length === 0) return;
    // Strip === lines and join content
    const content = disclaimerLines
      .filter(l => !/^[=]+$/.test(l.trim()))
      .map(l => l.replace(/^=+\s*|\s*=+$/g, '').trim())
      .filter(l => l.length > 0)
      .join('<br/>');
    if (content) {
      result.push(`<div class="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 my-3 text-yellow-300 text-xs sm:text-sm">${content}</div>`);
    }
    disclaimerLines = [];
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect disclaimer block start: line containing "ДИСКЛЕЙМЕР" or starts with ===== and next line has ДИСКЛЕЙМЕР/ВНИМАНИЕ
    if (!inDisclaimer && /ДИСКЛЕЙМЕР/i.test(trimmed)) {
      inDisclaimer = true;
      disclaimerLines = [line];
      continue;
    }
    if (!inDisclaimer && /^={3,}/.test(trimmed) && i + 1 < lines.length && /ДИСКЛЕЙМЕР|ВНИМАНИЕ/i.test(lines[i + 1].trim())) {
      inDisclaimer = true;
      disclaimerLines = [line];
      continue;
    }

    // Inside disclaimer block
    if (inDisclaimer) {
      disclaimerLines.push(line);
      // End disclaimer on closing === line (but not the opening one)
      if (/^={3,}$/.test(trimmed) && disclaimerLines.length > 2) {
        inDisclaimer = false;
        flushDisclaimer();
        continue;
      }
      // Also end if we hit a numbered section or empty line after content
      if (disclaimerLines.length > 6 && trimmed === '') {
        inDisclaimer = false;
        flushDisclaimer();
        continue;
      }
      continue;
    }

    // Match standalone ВНИМАНИЕ line (disclaimer without === wrapper)
    if (/^ВНИМАНИЕ:/i.test(trimmed)) {
      // Collect this and following non-empty lines as disclaimer
      disclaimerLines = [line];
      let j = i + 1;
      while (j < lines.length && lines[j].trim() !== '' && !/^[=]{3,}$/.test(lines[j].trim()) && !/^\d+\.\s/.test(lines[j].trim())) {
        disclaimerLines.push(lines[j]);
        j++;
      }
      // Check if next line is closing ===
      if (j < lines.length && /^={3,}$/.test(lines[j].trim())) {
        disclaimerLines.push(lines[j]);
        j++;
      }
      i = j - 1;
      flushDisclaimer();
      continue;
    }
    
    // Detect table start
    if (!inTable && (trimmed.startsWith('+') || (trimmed.startsWith('|') && trimmed.includes('|')))) {
      inTable = true;
      tableLines = [line];
      continue;
    }
    
    // Continue collecting table lines
    if (inTable) {
      if (trimmed.startsWith('+') || trimmed.startsWith('|') || /^[\-=]+$/.test(trimmed)) {
        tableLines.push(line);
        continue;
      } else {
        // Table ended
        result.push(processTable(tableLines));
        tableLines = [];
        inTable = false;
      }
    }
    
    // Format headers (lines with only === or ---)
    if (/^[=]+$/.test(trimmed)) {
      continue; // Skip pure separator lines
    }
    
    // Format section headers (ALL CAPS with numbers)
    if (/^(\d+\.\s+)?[A-ZА-ЯЁ\s,\-:]{10,}$/.test(trimmed) && !trimmed.includes('|')) {
      result.push(`<h3 class="text-cyan-400 font-semibold mt-4 mb-2 text-sm sm:text-base">${trimmed}</h3>`);
      continue;
    }
    
    // Regular text
    result.push(line);
  }
  
  // Process any remaining table or disclaimer
  if (tableLines.length > 0) {
    result.push(processTable(tableLines));
  }
  if (disclaimerLines.length > 0) {
    flushDisclaimer();
  }
  
  return result.join('\n');
};

// Inject inline PubMed citation badges: replace [N] with linked superscript
const injectCitationLinks = (html, citations) => {
  if (!citations || citations.length === 0) return html;
  const map = new Map(citations.map(c => [String(c.idx), c]));
  // Match [N] only when N is a number (1-99). Avoid matching inside HTML tags.
  return html.replace(/\[(\d{1,2})\](?![^<]*>)/g, (m, n) => {
    const cit = map.get(n);
    if (!cit || !cit.url) return m;
    const safeTitle = (cit.title || `PubMed PMID: ${cit.pmid}`).replace(/"/g, '&quot;');
    return `<a href="${cit.url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center align-super text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-cyan-500/20 hover:bg-cyan-500/35 text-cyan-300 hover:text-cyan-200 border border-cyan-500/30 mx-0.5 transition-colors no-underline" title="${safeTitle}" data-testid="citation-${n}">[${n}]</a>`;
  });
};

// Component to render formatted medical content
const MedicalContent = ({ content, citations }) => {
  const formattedContent = formatMedicalContent(content);
  
  // Check if content has HTML
  const hasHTML = /<[a-z][\s\S]*>/i.test(formattedContent);
  
  if (hasHTML || (citations && citations.length > 0)) {
    let html = (hasHTML ? formattedContent : formattedContent).replace(/\n/g, '<br/>');
    html = injectCitationLinks(html, citations);
    return (
      <div 
        className="text-sm sm:text-base leading-relaxed break-words medical-content"
        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  
  return (
    <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words" 
       style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
      {content}
    </p>
  );
};

// Deep Research step icons
const stepIcons = {
  extract: FileText,
  pubmed: Search,
  protocols: Shield,
  drugbank: Pill,
  graph: Activity,
  generate: Brain,
};

// Deep Research Steps Component - visionOS Dark Style
const ResearchSteps = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="research-steps">
      {steps.map((step) => {
        const Icon = stepIcons[step.id] || Microscope;
        const isDone = step.status === 'done';
        const isActive = step.status === 'active';
        return (
          <div key={step.id} className={`flex items-center gap-2.5 sm:gap-3 py-1.5 sm:py-2 transition-all duration-300 ${isDone ? 'opacity-60' : 'opacity-100'}`}>
            <div className={`relative w-6 h-6 sm:w-7 sm:h-7 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
              isDone ? 'bg-emerald-500/20' : isActive ? 'bg-cyan-500/20' : 'bg-white/[0.06]'
            }`}>
              {isDone ? <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" />
              : isActive ? <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400" />
              : <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/30" />}
              {isActive && (
                <span className="absolute inset-0 rounded-lg sm:rounded-xl border-2 border-cyan-400/50 border-t-transparent animate-spin" />
              )}
            </div>
            <span className={`text-xs font-medium transition-all duration-300 ${
              isDone ? 'text-emerald-400/70' : isActive ? 'text-cyan-300' : 'text-white/30'
            }`}>{step.label}</span>
            {isActive && <Loader2 className="w-3 h-3 text-cyan-400 animate-spin ml-auto flex-shrink-0" data-testid={`step-spinner-${step.id}`} />}
          </div>
        );
      })}
    </div>
  );
};

// Research Sources List - shows PubMed/ICD/OpenFDA studied articles like Deep Research
const ResearchSourcesList = ({ citations }) => {
  if (!citations || citations.length === 0) return null;
  return (
    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/[0.06]" data-testid="research-sources-list">
      <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
        <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
        <p className="text-[11px] sm:text-xs text-white/70 uppercase tracking-wider font-semibold">
          Изученные источники ({citations.length})
        </p>
      </div>
      <div className="space-y-1.5 sm:space-y-2">
        {citations.map((cit) => (
          <a
            key={cit.idx}
            href={cit.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-cyan-500/30 transition-all"
            data-testid={`source-link-${cit.idx}`}
          >
            <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center text-[10px] sm:text-xs font-semibold text-cyan-300">
              {cit.idx}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-white/85 group-hover:text-white leading-snug line-clamp-2">
                {cit.title || `PubMed PMID: ${cit.pmid}`}
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold text-blue-300/70">
                  {cit.type === 'pubmed' ? 'PubMed' : cit.type || 'Источник'}
                </span>
                {cit.pmid && (
                  <span className="text-[9px] sm:text-[10px] text-white/40">• PMID: {cit.pmid}</span>
                )}
                <ExternalLink className="w-2.5 h-2.5 text-white/30 group-hover:text-cyan-400 ml-auto" />
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

// Sources display - visionOS Dark Style
const SourcesBadges = ({ sources }) => {
  if (!sources || sources.length === 0) return null;
  const sourceIcons = {
    pubmed: { icon: Search, color: 'text-blue-300', bg: 'bg-blue-500/15 border-blue-500/25' },
    protocols: { icon: Shield, color: 'text-emerald-300', bg: 'bg-emerald-500/15 border-emerald-500/25' },
    drugbank: { icon: Pill, color: 'text-purple-300', bg: 'bg-purple-500/15 border-purple-500/25' },
    vectors: { icon: Activity, color: 'text-cyan-300', bg: 'bg-cyan-500/15 border-cyan-500/25' },
  };
  return (
    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 sm:mt-4 pt-2.5 sm:pt-3 border-t border-white/[0.06]" data-testid="response-sources">
      <span className="text-[9px] sm:text-[10px] text-white/30 mr-1 self-center font-medium">Источники:</span>
      {sources.map((src, i) => {
        const style = sourceIcons[src.type] || sourceIcons.protocols;
        const Icon = style.icon;
        return (
          <span key={i} className={`inline-flex items-center gap-1 sm:gap-1.5 text-[9px] sm:text-[10px] font-medium ${style.color} ${style.bg} border rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1`}>
            <Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />{src.label}
          </span>
        );
      })}
    </div>
  );
};

const ChatInterface = ({ sessionId: initialSessionId, onFullscreen, onMinimize, isFullscreen = false }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(initialSessionId);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showSessionList, setShowSessionList] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showMedicalSummary, setShowMedicalSummary] = useState(false);
  const [medicalSummary, setMedicalSummary] = useState(null);
  const [loadingMedical, setLoadingMedical] = useState(false);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [showConsilium, setShowConsilium] = useState(false);

  // Stream state
  const [streamMode, setStreamMode] = useState(null); // 'simple' or 'document'
  const [streamProgress, setStreamProgress] = useState(0);
  const [researchSteps, setResearchSteps] = useState([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isThinking, setIsThinking] = useState(false); // simple mode: just thinking dots
  const [isResearching, setIsResearching] = useState(false); // document mode: deep research panel
  const [isStreaming, setIsStreaming] = useState(false); // text is streaming
  const [currentSources, setCurrentSources] = useState([]); // sources used in current response
  
  // Document processing state
  const [docTotal, setDocTotal] = useState(0);
  const [docProcessing, setDocProcessing] = useState([]); // array of {index, filename, status, error}
  const [chunkInfo, setChunkInfo] = useState(null); // {totalChunks, totalFiles} for multi-chunk processing
  const [currentCitations, setCurrentCitations] = useState([]); // PubMed citations for current response

  // SuperChat orchestrator state
  const [orchestratorPlan, setOrchestratorPlan] = useState(null); // {use_consilium, specialists, reasoning, ...}
  const [consiliumSpecialists, setConsiliumSpecialists] = useState([]); // list of {id, name_ru, emoji, status}

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem('lastChatSessionId', currentSessionId);
      loadMessages();
      loadAvailableFiles();
    }
    loadSessions();
  }, [currentSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, researchSteps]);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try { setSessions(await chatAPI.getSessions()); } catch (e) { console.error(e); }
    finally { setLoadingSessions(false); }
  };

  const loadMessages = async () => {
    try {
      const msgs = await chatAPI.getMessages(currentSessionId);
      // Add default sources to assistant messages loaded from DB
      const withSources = msgs.map(m => {
        if (m.role === 'assistant' && !m.sources) {
          return { ...m, 
            filesAnalyzed: m.files_analyzed || 0,
            citations: m.citations || [],
            sources: m.has_files
            ? [{ type: 'protocols', label: 'Документы пациента' }, { type: 'pubmed', label: 'PubMed' }, { type: 'protocols', label: 'ESC/ADA/KDIGO' }, { type: 'drugbank', label: 'DrugBank' }, { type: 'vectors', label: '15 векторов здоровья' }]
            : [{ type: 'protocols', label: 'Evidence-Based Medicine' }]
          };
        }
        return m;
      });
      setMessages(withSources);
    } catch (e) { console.error(e); }
  };

  const loadAvailableFiles = async () => {
    try {
      const records = await medicalAPI.getRecords();
      const files = [];
      records.forEach(r => {
        r.files?.forEach(f => files.push({ ...f, recordDate: r.date, recordType: r.record_type }));
      });
      setAvailableFiles(files);
    } catch (e) { console.error(e); }
  };

  const loadMedicalSummary = async () => {
    setLoadingMedical(true);
    try { setMedicalSummary(await medicalAPI.getMedicalSummary()); setShowMedicalSummary(true); }
    catch (e) { toast({ title: 'Ошибка', description: 'Не удалось загрузить мед. карту', variant: 'destructive' }); }
    finally { setLoadingMedical(false); }
  };

  const createNewSession = async (customTitle = null) => {
    try {
      const s = await chatAPI.createSession(customTitle);
      setSessions([s, ...sessions]); setCurrentSessionId(s.id); setMessages([]);
      setShowSessionList(false); setShowNewChatDialog(false); setNewChatTitle('');
    } catch (e) { toast({ title: 'Ошибка', description: 'Не удалось создать чат', variant: 'destructive' }); }
  };

  const saveTitle = async (sessionId) => {
    if (!editingTitle.trim()) return;
    try {
      await chatAPI.updateSessionTitle(sessionId, editingTitle.trim());
      setSessions(sessions.map(s => s.id === sessionId ? { ...s, title: editingTitle.trim() } : s));
      setEditingSessionId(null); setEditingTitle('');
    } catch (e) { toast({ title: 'Ошибка', variant: 'destructive' }); }
  };

  const switchSession = (sessionId) => { setCurrentSessionId(sessionId); setMessages([]); setShowSessionList(false); };

  const getSessionPreview = (session) => {
    const d = new Date(session.created_at);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = d.toDateString() === today.toDateString() ? 'Сегодня'
      : d.toDateString() === yesterday.toDateString() ? 'Вчера'
      : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    return { title: session.title || `Чат от ${dateStr}`, time: d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) };
  };

  const resetStream = useCallback(() => {
    setIsSending(false); setIsThinking(false); setIsResearching(false); setIsStreaming(false);
    setStreamingContent(''); setStreamProgress(0); setResearchSteps([]); setStreamMode(null);
    setCurrentSources([]); setDocTotal(0); setDocProcessing([]); setChunkInfo(null);
    setCurrentCitations([]);
    setOrchestratorPlan(null); setConsiliumSpecialists([]);
    abortControllerRef.current = null;
  }, []);

  const handleStopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      toast({ title: 'Генерация остановлена', description: 'Частичный ответ сохранён в чате' });
    }
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    const fileIds = attachedFiles.map(f => f.id);
    const hadFiles = attachedFiles.length > 0;

    // Clear files immediately after send
    if (hadFiles) setAttachedFiles([]);

    setMessages(prev => [...prev, {
      id: Date.now().toString(), role: 'user', content: userMessage,
      timestamp: new Date().toISOString(), hasFiles: hadFiles
    }]);

    setIsSending(true);
    setStreamingContent('');
    setResearchSteps([]);
    setStreamProgress(0);
    setCurrentSources([]);
    setCurrentCitations([]);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    let fullResponse = '';
    let aiMessageId = null;
    let actualFilesAnalyzed = 0;
    let disconnected = false;
    const collectedSources = [];
    let collectedCitations = [];

    // Source labels for step IDs
    const sourceLabels = {
      extract: { type: 'protocols', label: 'Документы пациента' },
      pubmed: { type: 'pubmed', label: 'PubMed' },
      protocols: { type: 'protocols', label: 'ESC/ADA/KDIGO' },
      drugbank: { type: 'drugbank', label: 'DrugBank' },
      graph: { type: 'vectors', label: '15 векторов здоровья' },
    };

    try {
      await fastChatAPI.stream(currentSessionId, userMessage, fileIds, {
        onUserMessageId: () => {},
        onMode: (mode) => {
          setStreamMode(mode);
          if (mode === 'document') { setIsResearching(true); }
          else { setIsThinking(true); }
        },
        onStep: (step) => {
          if (step.status === 'done' && sourceLabels[step.id]) {
            const src = sourceLabels[step.id];
            if (!collectedSources.find(s => s.type === src.type && s.label === src.label)) {
              collectedSources.push(src);
              setCurrentSources([...collectedSources]);
            }
          }
          setResearchSteps(prev => {
            const existing = prev.findIndex(s => s.id === step.id);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = step;
              return updated;
            }
            return [...prev, step];
          });
        },
        onProgress: (value) => { setStreamProgress(value); },
        onChunk: (chunk) => {
          if (!fullResponse) {
            setIsThinking(false);
            setIsResearching(false);
            setIsStreaming(true);
          }
          fullResponse += chunk;
          setStreamingContent(fullResponse);
        },
        onComplete: (msgId, filesCount) => { 
          aiMessageId = msgId; 
          // Store the actual files count from backend
          if (filesCount !== undefined) {
            actualFilesAnalyzed = filesCount;
          }
        },
        onError: (error) => { toast({ title: 'Ошибка', description: error, variant: 'destructive' }); },
        onStatus: () => {},
        onSource: () => {},
        // New document processing callbacks
        onDocTotal: (count) => { setDocTotal(count); setDocProcessing([]); },
        onDocStart: (data) => {
          setDocProcessing(prev => [...prev, {
            index: data.index,
            filename: data.filename,
            status: 'processing',
            label: data.label
          }]);
        },
        onDocDone: (data) => {
          setDocProcessing(prev => prev.map(doc =>
            doc.index === data.index
              ? { ...doc, status: data.success ? 'done' : 'error', error: data.error, label: data.label }
              : doc
          ));
        },
        onChunkInfo: (info) => { setChunkInfo(info); },
        onCitations: (items) => { collectedCitations = items || []; setCurrentCitations(items || []); },
        onPlan: (plan) => {
          setOrchestratorPlan(plan);
          // Pre-populate consilium card with pending specialists
          if (plan?.use_consilium && plan?.specialists) {
            setConsiliumSpecialists(plan.specialists.map(sid => ({
              id: sid, name_ru: sid, status: 'pending'
            })));
          }
          // If complex flow, switch UI to research mode
          if (plan?.use_rag || plan?.use_consilium) {
            setIsResearching(true);
            setIsThinking(false);
          }
        },
        onConsiliumSpecialists: (items) => {
          setConsiliumSpecialists(prev => {
            // Merge new metadata (name_ru, emoji)
            const map = new Map(prev.map(s => [s.id, s]));
            items?.forEach(s => {
              const existing = map.get(s.id) || {};
              map.set(s.id, { ...existing, ...s, status: existing.status || 'pending' });
            });
            return Array.from(map.values());
          });
        },
        onSpecialistStart: (data) => {
          setConsiliumSpecialists(prev => {
            const map = new Map(prev.map(s => [s.id, s]));
            map.set(data.id, { ...(map.get(data.id) || {}), ...data, status: 'thinking' });
            return Array.from(map.values());
          });
        },
        onSpecialistDone: (data) => {
          setConsiliumSpecialists(prev => prev.map(s =>
            s.id === data.id
              ? { ...s, ...data, status: data.success ? 'done' : 'error' }
              : s
          ));
        },
        onSynthesisStart: () => {
          // Final synthesis is starting — switch from research panel to streaming text
          setIsThinking(false);
          setIsResearching(false);
          setIsStreaming(true);
        },
      }, abortControllerRef.current?.signal);

      // Add message locally for instant display (if streaming worked)
      if (fullResponse) {
        const filesCount = actualFilesAnalyzed > 0 ? actualFilesAnalyzed : (hadFiles ? fileIds.length : 0);
        setMessages(prev => [...prev, {
          id: aiMessageId || Date.now().toString(), role: 'assistant', content: fullResponse.trim(),
          timestamp: new Date().toISOString(), isRAG: true, filesAnalyzed: filesCount,
          citations: collectedCitations,
          sources: hadFiles ? collectedSources : [{ type: 'protocols', label: 'Evidence-Based Medicine' }]
        }]);
      }
    } catch (error) {
      // Distinguish abort vs real error
      if (error?.name === 'AbortError') {
        // User stopped — partial response was already streamed; loadMessages() will pick up DB state
      } else {
        disconnected = true;
        // Network/proxy timeout — backend continues processing in the background and
        // saves to DB. We poll for it below in the finally block.
        toast({
          title: 'Соединение прервано',
          description: 'Обработка продолжается на сервере. Подождите, ответ скоро появится...',
        });
      }
    } finally {
      // ALWAYS reload messages from DB — the definitive safety net.
      try { await loadMessages(); } catch(e) {}

      // === BACKGROUND-RECOVERY POLLING ===
      // If the connection dropped (proxy 60s timeout) but the backend is still
      // synthesizing in a background task, poll the DB until the AI message
      // has been fully saved (streaming=false). Up to 3 minutes.
      // Trigger whenever we got disconnected without a complete response,
      // regardless of whether aiMessageId was emitted before the drop.
      const responseIncomplete = !fullResponse || fullResponse.length < 50;
      if (disconnected && hadFiles && responseIncomplete) {
        let pollAttempts = 0;
        const maxAttempts = 60; // 60 × 3s = 3 min
        const sessionAtPoll = currentSessionId;
        const pollInterval = setInterval(async () => {
          pollAttempts++;
          try {
            const msgs = await chatAPI.getMessages(sessionAtPoll);
            const lastMsg = msgs && msgs.length > 0 ? msgs[msgs.length - 1] : null;
            if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content && !lastMsg.streaming) {
              clearInterval(pollInterval);
              if (currentSessionId === sessionAtPoll) await loadMessages();
              toast({ title: 'Готово', description: 'Ответ получен из фоновой обработки' });
            } else if (pollAttempts >= maxAttempts) {
              clearInterval(pollInterval);
            }
          } catch (e) {
            if (pollAttempts >= maxAttempts) clearInterval(pollInterval);
          }
        }, 3000);
      }

      resetStream();
      loadSessions();
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const blob = await chatAPI.exportToPdf(currentSessionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `AIMedGuru_Chat_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) { toast({ title: 'Ошибка', variant: 'destructive' }); }
    finally { setIsExporting(false); }
  };

  const toggleFileAttachment = (file) => {
    if (attachedFiles.some(f => f.id === file.id)) {
      setAttachedFiles(prev => prev.filter(f => f.id !== file.id));
    } else {
      if (attachedFiles.length >= 50) { toast({ title: 'Максимум 50 файлов', variant: 'destructive' }); return; }
      setAttachedFiles(prev => [...prev, file]);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    if (attachedFiles.length + files.length > 50) { toast({ title: 'Максимум 50 файлов', variant: 'destructive' }); return; }
    setIsUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file); fd.append('record_type', 'analysis');
        fd.append('date', new Date().toISOString().split('T')[0]);
        const resp = await medicalAPI.addRecord(fd);
        if (resp.files?.length > 0) setAttachedFiles(prev => [...prev, { ...resp.files[0], recordDate: resp.date, recordType: resp.record_type }]);
      }
      toast({ title: `Загружено: ${files.length}` });
      await loadAvailableFiles();
    } catch (e) { toast({ title: 'Ошибка загрузки', variant: 'destructive' }); }
    finally { setIsUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const getRecordTypeLabel = (t) => ({ consultation: 'Консультация', analysis: 'Анализ', prescription: 'Рецепт' }[t] || t);

  // ==================== SESSION LIST - visionOS Dark Style ====================
  if (showSessionList) {
    return (
      <div className="flex flex-col h-full bg-transparent" data-testid="session-list">
        <div className="flex items-center justify-between p-3 sm:p-4 bg-white/[0.04] border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowSessionList(false)} variant="ghost" size="sm" className="text-white/70 hover:bg-white/[0.08] h-9 w-9 sm:h-10 sm:w-10 p-0 rounded-xl"><ChevronLeft className="w-5 h-5" /></Button>
            <h3 className="text-white font-medium text-base sm:text-lg">История чатов</h3>
          </div>
          <Button onClick={() => setShowNewChatDialog(true)} size="sm" className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/30 rounded-full h-9 sm:h-10 px-4 text-sm"><Plus className="w-4 h-4 mr-1.5" />Новый</Button>
        </div>
        <ScrollArea className="flex-1 px-3 sm:px-4">
          <div className="space-y-2 sm:space-y-3 py-3 sm:py-4">
            {loadingSessions ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
            : sessions.length === 0 ? <div className="text-center py-16"><MessageSquare className="w-10 h-10 text-white/20 mx-auto mb-3" /><p className="text-white/40 font-medium">Нет чатов</p></div>
            : sessions.map((session) => {
                const preview = getSessionPreview(session);
                const isActive = session.id === currentSessionId;
                const isEditing = editingSessionId === session.id;
                return (
                  <div key={session.id} className={`w-full p-3 sm:p-4 rounded-2xl transition-all border ${isActive ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`}>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Input value={editingTitle} onChange={(e) => setEditingTitle(e.target.value)} className="flex-1 h-10 bg-white/[0.08] border-white/[0.15] text-white rounded-full text-sm px-4" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(session.id); if (e.key === 'Escape') { setEditingSessionId(null); setEditingTitle(''); }}} />
                        <Button onClick={() => saveTitle(session.id)} size="sm" className="h-10 w-10 p-0 bg-emerald-500/20 text-emerald-400 rounded-xl"><Check className="w-4 h-4" /></Button>
                        <Button onClick={() => { setEditingSessionId(null); setEditingTitle(''); }} size="sm" className="h-10 w-10 p-0 bg-white/[0.06] text-white/60 rounded-xl"><X className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <button onClick={() => switchSession(session.id)} className="w-full text-left">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-cyan-500/30' : 'bg-white/[0.08]'}`}>
                              <MessageSquare className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-cyan-300' : 'text-white/50'}`} />
                            </div>
                            <div className="min-w-0 flex-1"><p className="text-white text-sm font-medium truncate">{preview.title}</p><p className="text-white/40 text-xs mt-0.5">{preview.time}</p></div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button onClick={(e) => { e.stopPropagation(); setEditingSessionId(session.id); setEditingTitle(preview.title); }} size="sm" className="h-8 w-8 p-0 bg-white/[0.06] hover:bg-white/[0.1] text-white/50 border-0 rounded-lg"><Edit2 className="w-3.5 h-3.5" /></Button>
                            {isActive && <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full" />}
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                );
              })
            }
          </div>
        </ScrollArea>
      </div>
    );
  }

  // ==================== MAIN CHAT - visionOS Dark ====================
  return (
    <div className="flex flex-col h-full bg-transparent" data-testid="chat-interface">
      {/* Header - iOS Glass */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3" style={{ background: 'rgba(118, 118, 128, 0.12)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button onClick={() => setShowSessionList(true)} size="sm" className="ios-glass-btn text-white/70 rounded-full w-9 h-9 sm:w-10 sm:h-10 p-0" data-testid="chat-history-btn"><BookOpen className="w-4 h-4 sm:w-5 sm:h-5" /></Button>
          <Button onClick={loadMedicalSummary} size="sm" className="ios-glass-btn text-white/70 rounded-full w-9 h-9 sm:w-10 sm:h-10 p-0" data-testid="medical-card-btn" disabled={loadingMedical}>{loadingMedical ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />}</Button>
          <div className="hidden sm:block ml-2">
            <h3 className="text-white font-semibold text-sm leading-tight">AIMEDGURU</h3>
            <p className="text-white/40 text-xs">Клинический аудит</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Minimize button (fullscreen mode) or Fullscreen button (normal mode) */}
          {isFullscreen && onMinimize ? (
            <Button onClick={onMinimize} size="sm" className="ios-glass-btn text-white/70 rounded-full px-4 h-9 sm:h-10 text-xs sm:text-sm" data-testid="chat-minimize-btn">
              <Minimize2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Свернуть</span>
            </Button>
          ) : onFullscreen ? (
            <Button onClick={onFullscreen} size="sm" className="ios-glass-btn text-white/70 rounded-full w-9 h-9 sm:w-10 sm:h-10 p-0" data-testid="chat-fullscreen-btn" title="Развернуть"><Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" /></Button>
          ) : null}
          <Button onClick={() => setShowNewChatDialog(true)} size="sm" className="ios-glass-btn text-white/70 rounded-full px-3 sm:px-4 h-9 sm:h-10 text-xs sm:text-sm"><Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Новый</span></Button>
          <Button onClick={handleExportPdf} disabled={isExporting || messages.length === 0} size="sm" className="ios-glass-btn text-cyan-300 rounded-full px-3 sm:px-4 h-9 sm:h-10 text-xs sm:text-sm">{isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">PDF</span></>}</Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-3 sm:px-5">
        <div className="space-y-3 sm:space-y-4 py-4 sm:py-6">
          {messages.length === 0 && !isSending && (
            <div className="text-center py-12 sm:py-16" data-testid="empty-chat">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-5">
                <Brain className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <p className="text-white text-lg sm:text-xl font-semibold">AIMEDGURU</p>
              <p className="text-white/40 text-sm mt-2 max-w-sm mx-auto px-4">Задайте вопрос или прикрепите анализы для клинического аудита</p>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.role}`}>
              <div className={`max-w-[90%] sm:max-w-[75%] rounded-[28px] sm:rounded-[32px] px-5 sm:px-6 py-4 sm:py-5 ${
                msg.role === 'user' 
                  ? 'text-white' 
                  : 'text-white/90'
              }`} style={msg.role === 'user' 
                ? { background: 'rgba(10, 132, 255, 0.35)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(10, 132, 255, 0.25)' }
                : { background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.12)' }
              }>
                {msg.role === 'assistant' && (
                  <div className="flex items-center flex-wrap gap-2 mb-2 sm:mb-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(48, 209, 88, 0.3)' }}>
                      <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                    </div>
                    <span className="text-xs text-white font-semibold">AIMEDGURU</span>
                    <span className="text-[10px] text-emerald-400 px-2 py-0.5 rounded-full flex items-center gap-1 font-medium" style={{ background: 'rgba(48, 209, 88, 0.15)' }}>
                      <CheckCircle2 className="w-2.5 h-2.5" />EBM
                    </span>
                    {msg.filesAnalyzed > 0 && (
                      <span className="text-[10px] text-cyan-300 px-2 py-0.5 rounded-full flex items-center gap-1 font-medium" style={{ background: 'rgba(10, 132, 255, 0.15)', border: '1px solid rgba(10, 132, 255, 0.2)' }}>
                        <FileText className="w-2.5 h-2.5" />
                        Проанализировано: {msg.filesAnalyzed} док.
                      </span>
                    )}
                  </div>
                )}
                {msg.hasFiles && <div className="flex items-center gap-1.5 text-xs text-cyan-300/70 mb-1.5"><FileText className="w-3.5 h-3.5" /><span>С документами</span></div>}
                {msg.role === 'assistant' ? (
                  <MedicalContent content={msg.content} citations={msg.citations} />
                ) : (
                  <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                )}
                {msg.role === 'assistant' && <SourcesBadges sources={msg.sources} />}
                {msg.role === 'assistant' && <ResearchSourcesList citations={msg.citations} />}
                <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-cyan-300/50' : 'text-white/30'}`}>{new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>
          ))}

          {/* Thinking indicator - iOS Glass OVAL */}
          {isThinking && (
            <div className="flex justify-start" data-testid="thinking-indicator">
              <div className="rounded-[28px] px-5 sm:px-6 py-4 sm:py-5" style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.12)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(10, 132, 255, 0.3)' }}>
                    <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white animate-pulse" />
                  </div>
                  <span className="text-sm text-white/60 font-medium">Анализирую...</span>
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Research Panel - iOS Glass OVAL */}
          {isResearching && (
            <div className="flex justify-start" data-testid="research-panel">
              <div className="max-w-[95%] sm:max-w-[80%] rounded-[28px] sm:rounded-[32px] px-5 sm:px-6 py-5 sm:py-6 space-y-3 sm:space-y-4" style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.12)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center" style={{ background: 'rgba(10, 132, 255, 0.3)' }}>
                    <Microscope className="w-5 h-5 sm:w-6 sm:h-6 text-white animate-pulse" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-semibold">Глубокий анализ</p>
                    <p className="text-xs text-white/40">
                      {docTotal > 0 ? chunkInfo && chunkInfo.totalChunks > 1 ? `${docTotal} документов • Пакет ${Math.min(Math.floor(docProcessing.length / (docTotal / chunkInfo.totalChunks)) + 1, chunkInfo.totalChunks)}/${chunkInfo.totalChunks}` : `Обработка ${docTotal} документов` : 'Проверка источников и протоколов'}
                    </p>
                  </div>
                </div>
                <div className="space-y-1.5" data-testid="progress-bar">
                  <div className="w-full h-2 sm:h-2.5 bg-white/[0.08] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${streamProgress}%` }} />
                  </div>
                  <p className="text-[10px] text-white/30 text-right font-medium">{streamProgress}%</p>
                </div>
                {docProcessing.length > 0 && (
                  <div className="space-y-1.5 sm:space-y-2 max-h-40 overflow-y-auto" data-testid="doc-processing-list">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Документы:</p>
                    {docProcessing.map((doc) => (
                      <div key={doc.index} className={`flex items-center gap-2 sm:gap-2.5 py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                        doc.status === 'processing' ? 'bg-cyan-500/10 border border-cyan-500/20' : doc.status === 'done' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
                      }`}>
                        {doc.status === 'processing' ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400 animate-spin flex-shrink-0" />
                        : doc.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400 flex-shrink-0" />
                        : <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-400 flex-shrink-0" />}
                        <span className={`text-xs font-medium truncate ${doc.status === 'processing' ? 'text-cyan-300' : doc.status === 'done' ? 'text-emerald-300' : 'text-red-300'}`}>
                          {doc.filename.length > 30 ? doc.filename.substring(0, 30) + '...' : doc.filename}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {consiliumSpecialists.length > 0 && (
                  <div className="space-y-1.5 sm:space-y-2" data-testid="consilium-list">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Обработка данных:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
                      {consiliumSpecialists.map((s) => (
                        <div key={s.id} className={`flex items-center gap-2 py-1.5 sm:py-2 px-2.5 sm:px-3 rounded-xl border transition-all duration-300 ${
                          s.status === 'thinking' ? 'bg-cyan-500/10 border-cyan-500/25' :
                          s.status === 'done' ? 'bg-emerald-500/10 border-emerald-500/25' :
                          s.status === 'error' ? 'bg-red-500/10 border-red-500/25' :
                          'bg-white/[0.04] border-white/[0.08]'
                        }`}>
                          <Brain className={`w-3.5 h-3.5 flex-shrink-0 ${
                            s.status === 'thinking' ? 'text-cyan-400' :
                            s.status === 'done' ? 'text-emerald-400' :
                            s.status === 'error' ? 'text-red-400' : 'text-white/40'
                          }`} />
                          <span className="text-[10px] sm:text-xs font-medium text-white/85 truncate flex-1">{s.name_ru || s.id}</span>
                          {s.status === 'thinking' && <Loader2 className="w-3 h-3 text-cyan-400 animate-spin flex-shrink-0" />}
                          {s.status === 'done' && <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />}
                          {s.status === 'error' && <X className="w-3 h-3 text-red-400 flex-shrink-0" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <ResearchSteps steps={researchSteps} />
              </div>
            </div>
          )}

          {/* Streaming Response */}
          {isStreaming && streamingContent && (
            <div className="flex justify-start" data-testid="streaming-response">
              <div className="max-w-[90%] sm:max-w-[75%] bg-white/[0.05] border border-white/[0.08] rounded-2xl sm:rounded-3xl px-4 sm:px-5 py-3 sm:py-4">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <Brain className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white animate-pulse" />
                  </div>
                  <span className="text-xs text-white font-semibold">AIMEDGURU</span>
                </div>
                <div className="text-sm sm:text-base text-white/90 leading-relaxed">
                  <MedicalContent content={streamingContent} citations={currentCitations} />
                  <span className="inline-block w-0.5 h-4 bg-cyan-400 ml-0.5 animate-pulse rounded-sm" />
                </div>
                {currentSources.length > 0 && <SourcesBadges sources={currentSources} />}
                {currentCitations.length > 0 && <ResearchSourcesList citations={currentCitations} />}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Attached Files - iOS Glass */}
      {attachedFiles.length > 0 && (
        <div className="px-3 sm:px-4 py-2.5 sm:py-3" style={{ background: 'rgba(10, 132, 255, 0.08)', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }} data-testid="attached-files">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(10, 132, 255, 0.3)' }}>
              <Brain className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-300" />
            </div>
            <span className="text-xs text-cyan-300 font-semibold">Документы ({attachedFiles.length})</span>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {attachedFiles.map((file) => (
              <div key={file.id} className="flex items-center gap-1.5 sm:gap-2 rounded-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs" style={{ background: 'rgba(118, 118, 128, 0.24)', border: '1px solid rgba(10, 132, 255, 0.2)' }}>
                <FileText size={12} className="text-cyan-300 flex-shrink-0 sm:w-[14px] sm:h-[14px]" />
                <span className="text-white truncate max-w-[80px] sm:max-w-[150px] font-medium">{file.filename}</span>
                <button onClick={() => toggleFileAttachment(file)} className="text-white/40 hover:text-red-400 ml-0.5 transition-colors"><X size={12} className="sm:w-[14px] sm:h-[14px]" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input - iOS Glass Oval */}
      <div className="p-3 sm:p-4" style={{ background: 'rgba(118, 118, 128, 0.08)', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <form onSubmit={handleSendMessage} className="flex gap-2 sm:gap-3 items-end rounded-full px-2 sm:px-3 py-1.5 sm:py-2 transition-all focus-within:border-cyan-500/30" style={{ background: 'rgba(118, 118, 128, 0.24)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Dialog open={showFileDialog} onOpenChange={setShowFileDialog}>
            <DialogTrigger asChild>
              <Button type="button" size="icon" className="bg-transparent hover:bg-white/[0.08] text-white/50 hover:text-white/70 rounded-full flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10 transition-colors" disabled={isSending} data-testid="attach-files-btn"><Paperclip className="w-4 h-4 sm:w-5 sm:h-5" /></Button>
            </DialogTrigger>
            <DialogContent className="text-white w-[95vw] max-w-lg mx-auto rounded-[22px] max-h-[80vh] flex flex-col p-0" style={{ background: 'rgba(28, 28, 30, 0.85)', backdropFilter: 'blur(40px) saturate(1.8)', WebkitBackdropFilter: 'blur(40px) saturate(1.8)', border: '1px solid rgba(255, 255, 255, 0.08)' }} data-testid="file-dialog">
              <DialogHeader className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><DialogTitle className="text-white font-semibold text-base sm:text-lg">Прикрепить документы</DialogTitle></DialogHeader>
              <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 sm:py-4 space-y-3 sm:space-y-4">
                <div>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="w-full text-cyan-300 rounded-full py-5 sm:py-6" style={{ background: 'rgba(10, 132, 255, 0.2)', border: '1px solid rgba(10, 132, 255, 0.25)' }}>
                    {isUploading ? <><Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />Загрузка...</> : <><Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />Загрузить с устройства</>}
                  </Button>
                </div>
                {availableFiles.length === 0 ? <p className="text-white/40 text-sm text-center py-6 sm:py-8">Нет файлов</p>
                : <div className="space-y-1.5 sm:space-y-2">{availableFiles.map((file) => {
                    const sel = attachedFiles.some(f => f.id === file.id);
                    return (
                      <button key={file.id} onClick={() => toggleFileAttachment(file)} className="w-full flex items-center gap-3 p-3 sm:p-4 rounded-2xl transition-all text-left" style={sel ? { background: 'rgba(10, 132, 255, 0.15)', border: '1px solid rgba(10, 132, 255, 0.25)' } : { background: 'rgba(118, 118, 128, 0.18)', border: '1px solid rgba(255, 255, 255, 0.06)' }} data-testid={`file-item-${file.id}`}>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={sel ? { background: 'rgba(10, 132, 255, 0.3)' } : { background: 'rgba(118, 118, 128, 0.24)' }}><FileText size={18} className={`sm:w-5 sm:h-5 ${sel ? 'text-cyan-300' : 'text-white/50'}`} /></div>
                        <div className="min-w-0 flex-1"><p className="text-sm text-white font-medium truncate">{file.filename}</p><p className="text-xs text-white/40">{getRecordTypeLabel(file.recordType)} - {file.recordDate}</p></div>
                        {sel && <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400 flex-shrink-0" />}
                      </button>);
                  })}</div>}
              </div>
              <div className="px-4 sm:px-5 py-3 sm:py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}><Button onClick={() => setShowFileDialog(false)} className="w-full text-white rounded-full h-10 sm:h-12 text-sm font-medium" style={{ background: 'rgba(118, 118, 128, 0.24)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>Готово ({attachedFiles.length})</Button></div>
            </DialogContent>
          </Dialog>

          <textarea value={inputMessage} onChange={(e) => { setInputMessage(e.target.value); e.target.style.height = '36px'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
            placeholder={attachedFiles.length > 0 ? 'Вопрос по документам...' : 'Задайте медицинский вопрос...'}
            className="bg-transparent border-0 text-white placeholder:text-white/40 focus:outline-none focus:ring-0 flex-1 text-sm sm:text-base resize-none py-2 px-2 sm:px-3 overflow-hidden caret-cyan-400"
            style={{ height: '36px', minHeight: '36px', maxHeight: '100px' }} disabled={isSending} rows={1}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); }}}
            data-testid="chat-input" />

          <Button 
            type={isSending ? "button" : "submit"}
            onClick={isSending ? handleStopGeneration : undefined}
            disabled={!isSending && !inputMessage.trim()}
            className={`rounded-full flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 transition-all ${isSending ? 'text-white' : inputMessage.trim() ? 'text-white' : 'text-white/60'}`}
            style={isSending ? { background: 'rgba(255, 69, 58, 0.85)' } : inputMessage.trim() ? { background: 'rgba(10, 132, 255, 0.8)' } : { background: 'rgba(118, 118, 128, 0.35)' }}
            size="icon"
            data-testid={isSending ? "stop-btn" : "send-btn"}
            title={isSending ? "Остановить генерацию" : "Отправить"}
          >
            {isSending ? <Square className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current" /> : <Send className="w-4 h-4 sm:w-5 sm:h-5" />}
          </Button>
        </form>
      </div>

      {/* Medical Summary Modal - Liquid Glass (matches app design) */}
      <Dialog open={showMedicalSummary} onOpenChange={setShowMedicalSummary}>
        <DialogContent className="text-white w-[95vw] max-w-2xl mx-auto rounded-[22px] max-h-[80vh] overflow-y-auto border-0 p-0" style={{ background: 'rgba(18, 20, 28, 0.92)', backdropFilter: 'blur(40px) saturate(1.8)', WebkitBackdropFilter: 'blur(40px) saturate(1.8)', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
          <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-2">
            <DialogTitle className="text-white font-semibold text-base sm:text-lg flex items-center gap-2.5">
              <div className="liquid-glass-icon w-9 h-9 flex items-center justify-center">
                <ClipboardList className="w-4.5 h-4.5 text-cyan-300" />
              </div>
              Медицинская карта
            </DialogTitle>
          </DialogHeader>
          {medicalSummary ? (
            <div className="space-y-4 px-5 sm:px-6 pb-5 sm:pb-6 mt-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="liquid-glass-card p-4 sm:p-5 text-center" data-testid="med-card-consultations">
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center mb-3" style={{ background: 'rgba(10, 132, 255, 0.18)' }}>
                      <Stethoscope className="w-5 h-5 text-cyan-300" />
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{medicalSummary.consultations?.length || 0}</p>
                    <p className="text-[11px] sm:text-xs text-white/55 font-medium mt-1">Консультаций</p>
                  </div>
                </div>
                <div className="liquid-glass-card p-4 sm:p-5 text-center" data-testid="med-card-analyses">
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center mb-3" style={{ background: 'rgba(48, 209, 88, 0.18)' }}>
                      <TestTube className="w-5 h-5 text-emerald-300" />
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{medicalSummary.analyses?.length || 0}</p>
                    <p className="text-[11px] sm:text-xs text-white/55 font-medium mt-1">Анализов</p>
                  </div>
                </div>
                <div className="liquid-glass-card p-4 sm:p-5 text-center" data-testid="med-card-prescriptions">
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center mb-3" style={{ background: 'rgba(175, 82, 222, 0.18)' }}>
                      <Pill className="w-5 h-5 text-purple-300" />
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{medicalSummary.prescriptions?.length || 0}</p>
                    <p className="text-[11px] sm:text-xs text-white/55 font-medium mt-1">Рецептов</p>
                  </div>
                </div>
              </div>
              {medicalSummary.total_records === 0 && (
                <div className="liquid-glass-card text-center py-8 sm:py-10 px-4">
                  <div className="relative z-10">
                    <ClipboardList className="w-8 h-8 mx-auto text-white/30 mb-3" />
                    <p className="text-white/55 text-sm">Мед. карта пуста</p>
                    <p className="text-white/35 text-xs mt-1">Загрузите документы в чат, чтобы AI заполнил карту</p>
                  </div>
                </div>
              )}
            </div>
          ) : <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/30" /></div>}
        </DialogContent>
      </Dialog>

      {/* New Chat Dialog - iOS Glass */}
      <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
        <DialogContent className="text-white w-[90vw] max-w-md mx-auto rounded-[22px]" style={{ background: 'rgba(28, 28, 30, 0.85)', backdropFilter: 'blur(40px) saturate(1.8)', WebkitBackdropFilter: 'blur(40px) saturate(1.8)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <DialogHeader><DialogTitle className="text-white font-semibold text-base sm:text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-cyan-400" />Новый чат</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Input value={newChatTitle} onChange={(e) => setNewChatTitle(e.target.value)} placeholder="Название (необязательно)" className="text-white placeholder:text-white/40 rounded-full h-11 sm:h-12 text-sm px-4" style={{ background: 'rgba(118, 118, 128, 0.24)', border: '1px solid rgba(255, 255, 255, 0.08)' }} onKeyDown={(e) => { if (e.key === 'Enter') createNewSession(newChatTitle.trim() || null); }} />
            <div className="flex gap-2 sm:gap-3">
              <Button onClick={() => createNewSession(newChatTitle.trim() || null)} className="flex-1 text-cyan-300 rounded-full h-11 sm:h-12 font-medium" style={{ background: 'rgba(10, 132, 255, 0.25)', border: '1px solid rgba(10, 132, 255, 0.3)' }}>Создать</Button>
              <Button onClick={() => { setShowNewChatDialog(false); setNewChatTitle(''); }} className="flex-1 text-white/70 rounded-full h-11 sm:h-12 font-medium" style={{ background: 'rgba(118, 118, 128, 0.24)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>Отмена</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Virtual Consilium Modal */}
      <ConsiliumModal
        open={showConsilium}
        onClose={() => setShowConsilium(false)}
        sessionId={currentSessionId}
        initialQuery={inputMessage}
      />
    </div>
  );
};

export default ChatInterface;
